import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getSessions } from '../../store/selector/session.selector';
import { getSessionBySessionKey } from '../../utils/session.utils';
import { SetCurrentlyWorkingSession } from '../../store/slice/session.slice';
import { CampaignTab } from '../../constants/common';
import CampaignTabs from '../../components/campaign/CampaignTabs';
import PlanTab from '../../components/campaign/tabs/PlanTab';
import CreateTab from '../../components/campaign/tabs/CreateTab';
import PublishTab from '../../components/campaign/tabs/PublishTab';
import { ICampaignAdDetails } from '../../interfaces/campaign-ads.interface';
import { SetCampaignAds } from '../../store/slice/campaign-ads.slice';
import { useLazyGetNewUserCampaignQuery } from '../../store/api/campaign.api';
import { setTemplateDetailsInCampaign } from '../../utils/campaign.utils';
import { useGetOrganizationTemplatesQuery } from '../../store/api/template.api';
import { getOrganizationId } from '../../store/selector/organization.selector';
import { AddBrandCampaign, SetSelectedCampaign } from '../../store/slice/campaign.slice';
import { getAdFromUrlActive, getAdFromUrlLoading } from '../../store/selector/ad-from-url.selector';
import { SetAdFromUrlLoading } from '../../store/slice/ad-from-url.slice';
import { getActiveBrand } from '../../store/selector/brand.selector';

interface IProps {
    store: any;
}

const Campaign = ({ store }: IProps) => {
    const naviagte = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const sessions = useAppSelector(getSessions);
    const activeBrand = useAppSelector(getActiveBrand);
    const organizationId = useAppSelector(getOrganizationId);
    const [fetchNewuserCampaign] = useLazyGetNewUserCampaignQuery();
    const adFromUrlFlowActive = useAppSelector(getAdFromUrlActive);
    const { data: organizationTemplates } = useGetOrganizationTemplatesQuery(
        organizationId ? organizationId : 0,
        {
            skip: !organizationId,
        },
    );

    const [loadingSession, setLoadingSession] = useState(true);
    const [activeTab, setActiveTab] = useState(CampaignTab.PLAN);
    const fetchCampaignAdsIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [generatingCampaign, setGeneratingCampaign] = useState(false);
    const [selectedCampaignAds, setSelectedCampaignAds] = useState<ICampaignAdDetails[]>([]);
    const [campaignAdsLoading, setCampaignAdsLoading] = useState(false);

    const resetCampign = () => {
        dispatch(SetCampaignAds({ ads: [], sessionKey: null }));
        if (fetchCampaignAdsIntervalRef.current) {
            clearInterval(fetchCampaignAdsIntervalRef.current);
            fetchCampaignAdsIntervalRef.current = null;
        }
        setSelectedCampaignAds([]);
        // setGeneratingCampaign(adFromUrlFlowActive ? true : false);
        setGeneratingCampaign(false);
        setCampaignAdsLoading(false);
    };

    useEffect(() => {
        setLoadingSession(true);
        resetCampign();
        const searchParams = new URLSearchParams(location.search);
        const queryValue = searchParams.get('q');

        if (queryValue) {
            const queriedSession = getSessionBySessionKey(sessions, queryValue);
            if (queriedSession) {
                dispatch(SetCurrentlyWorkingSession(queriedSession));
                setLoadingSession(false);
            } else {
                naviagte('/');
            }
        } else {
            naviagte('/');
        }
    }, [location.search]);

    useEffect(() => {
        const fetchNewUserCampaignData = async () => {
            try {
                if (!activeBrand) return;
                const response = await fetchNewuserCampaign(activeBrand.id);
                const data = response.data;
                if (data) {
                    const updatedCampaign = setTemplateDetailsInCampaign(
                        data,
                        organizationTemplates ? organizationTemplates : [],
                    );
                    dispatch(AddBrandCampaign(updatedCampaign));
                    dispatch(SetSelectedCampaign(updatedCampaign));
                }
                dispatch(SetAdFromUrlLoading(false));
            } catch (e) {
                dispatch(SetAdFromUrlLoading(false));
            }
        };

        if (adFromUrlFlowActive) {
            dispatch(SetAdFromUrlLoading(true));
            // setActiveTab(CampaignTab.CREATE);
            // setGeneratingCampaign(true);
            setActiveTab(CampaignTab.PLAN);
            setSelectedCampaignAds([]);
            fetchNewUserCampaignData();
        }
    }, [adFromUrlFlowActive]);

    return (
        <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-min-w-5xl tw-max-w-8xl">
            {!loadingSession && (
                <>
                    <div className="tw-sticky tw-top-0 tw-z-[90] tw-bg-white">
                        <CampaignTabs activeTab={activeTab} setActiveTab={setActiveTab} />
                    </div>

                    <div className="tw-flex-1 tw-overflow-y-auto tw-px-6">
                        {activeTab === CampaignTab.PLAN && (
                            <PlanTab
                                generatingCampaign={generatingCampaign}
                                setGeneratingCampaign={setGeneratingCampaign}
                                setActiveTab={setActiveTab}
                            />
                        )}
                        {activeTab === CampaignTab.CREATE && (
                            <CreateTab
                                store={store}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                generatingCampaign={generatingCampaign}
                                selectedCampaignAds={selectedCampaignAds}
                                setGeneratingCampaign={setGeneratingCampaign}
                                fetchCampaignAdsIntervalRef={fetchCampaignAdsIntervalRef}
                                setSelectedCampaignAds={setSelectedCampaignAds}
                                campaignAdsLoading={campaignAdsLoading}
                                setCampaignAdsLoading={setCampaignAdsLoading}
                            />
                        )}
                        {activeTab === CampaignTab.PUBLISH && (
                            <PublishTab
                                setActiveTab={setActiveTab}
                                selectedCampaignAds={selectedCampaignAds}
                                setSelectedCampaignAds={setSelectedCampaignAds}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Campaign;
